'use client';

import { useSyncExternalStore } from 'react';

type AuthSessionObserverStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'unauthenticated';

export type AuthSessionObserverSnapshot = {
  status: AuthSessionObserverStatus;
  currentUserId: string | null;
  source: string | null;
  transition?: AuthSessionTransitionState;
};

export type AuthSessionTransitionDirection = 'sign-in' | 'sign-out';

export const AUTH_SESSION_TRANSITION_SOURCES = {
  credentialAuth: 'credential-auth',
  googleIdentityButton: 'google-identity-button',
  socialLogin: 'social-login',
} as const;

export type AuthSessionTransitionState = {
  direction: AuthSessionTransitionDirection;
  id: number;
  phase: 'pending' | 'validating' | 'completed';
  source: string;
};

export type AuthSessionTransitionToken = {
  id: number;
};

type AuthSessionObserverPublicationOptions = {
  transitionToken?: AuthSessionTransitionToken;
};

type Listener = () => void;

const DEFAULT_SNAPSHOT: AuthSessionObserverSnapshot = {
  status: 'idle',
  currentUserId: null,
  source: null,
};

let snapshot = DEFAULT_SNAPSHOT;
let activeTransition:
  | {
      direction: AuthSessionTransitionDirection;
      id: number;
      phase: 'pending' | 'validating';
      previousSnapshot: AuthSessionObserverSnapshot;
      source: string;
    }
  | undefined;
let nextTransitionId = 1;
const listeners = new Set<Listener>();

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

function isSameSnapshot(
  left: AuthSessionObserverSnapshot,
  right: AuthSessionObserverSnapshot
) {
  return (
    left.status === right.status &&
    left.currentUserId === right.currentUserId &&
    left.source === right.source &&
    left.transition?.direction === right.transition?.direction &&
    left.transition?.id === right.transition?.id &&
    left.transition?.phase === right.transition?.phase &&
    left.transition?.source === right.transition?.source
  );
}

export function publishAuthSessionObserverSnapshot(
  nextSnapshot: AuthSessionObserverSnapshot,
  options: AuthSessionObserverPublicationOptions = {}
) {
  let nextTransition = nextSnapshot.transition;

  if (activeTransition) {
    if (
      activeTransition.phase === 'validating' &&
      options.transitionToken?.id !== activeTransition.id
    ) {
      return;
    }

    const targetReached =
      activeTransition.direction === 'sign-in'
        ? nextSnapshot.status === 'authenticated'
        : nextSnapshot.status === 'unauthenticated';

    if (!targetReached) {
      const validationFailed =
        activeTransition.phase === 'validating' &&
        (activeTransition.direction === 'sign-in'
          ? nextSnapshot.status === 'unauthenticated'
          : nextSnapshot.status === 'authenticated');
      if (validationFailed) {
        activeTransition = undefined;
        const failedValidationSnapshot = {
          ...nextSnapshot,
          transition: undefined,
        } satisfies AuthSessionObserverSnapshot;
        if (isSameSnapshot(snapshot, failedValidationSnapshot)) {
          return;
        }
        snapshot = failedValidationSnapshot;
        notifyListeners();
      }
      return;
    }

    nextTransition = {
      direction: activeTransition.direction,
      id: activeTransition.id,
      phase: 'completed',
      source: activeTransition.source,
    };
  } else if (
    snapshot.transition?.phase === 'completed' &&
    snapshot.status === nextSnapshot.status
  ) {
    nextTransition = snapshot.transition;
  }

  activeTransition = undefined;
  const normalizedSnapshot = {
    ...nextSnapshot,
    transition: nextTransition,
  };
  if (isSameSnapshot(snapshot, normalizedSnapshot)) {
    return;
  }

  snapshot = normalizedSnapshot;
  notifyListeners();
}

export function beginAuthSessionTransition(
  direction: AuthSessionTransitionDirection,
  source: string
): AuthSessionTransitionToken {
  const id = nextTransitionId;
  nextTransitionId += 1;
  activeTransition = {
    direction,
    id,
    phase: 'pending',
    previousSnapshot:
      activeTransition?.previousSnapshot ??
      ({
        ...snapshot,
        transition: undefined,
      } satisfies AuthSessionObserverSnapshot),
    source,
  };
  snapshot = {
    status: 'loading',
    currentUserId: null,
    source: `auth-transition:${direction}:${source}`,
    transition: {
      direction,
      id,
      phase: 'pending',
      source,
    },
  };
  notifyListeners();

  return { id };
}

export function cancelAuthSessionTransition(token: AuthSessionTransitionToken) {
  if (!activeTransition || activeTransition.id !== token.id) {
    return;
  }

  snapshot = activeTransition.previousSnapshot;
  activeTransition = undefined;
  notifyListeners();
}

export function markAuthSessionTransitionValidating(
  token: AuthSessionTransitionToken
) {
  if (
    !activeTransition ||
    activeTransition.id !== token.id ||
    activeTransition.phase === 'validating'
  ) {
    return;
  }

  activeTransition = {
    ...activeTransition,
    phase: 'validating',
  };
  snapshot = {
    ...snapshot,
    transition: {
      direction: activeTransition.direction,
      id: activeTransition.id,
      phase: 'validating',
      source: activeTransition.source,
    },
  };
  notifyListeners();
}

export function readAuthSessionTransitionDirection(
  value: AuthSessionObserverSnapshot
): AuthSessionTransitionDirection | null {
  return value.status === 'loading' &&
    value.transition &&
    value.transition.phase !== 'completed'
    ? value.transition.direction
    : null;
}

export function resetAuthSessionObserverSnapshotForTests() {
  if (process.env.NODE_ENV !== 'test') {
    return;
  }

  snapshot = DEFAULT_SNAPSHOT;
  activeTransition = undefined;
  nextTransitionId = 1;
  notifyListeners();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return snapshot;
}

function getServerSnapshot() {
  return DEFAULT_SNAPSHOT;
}

export function useAuthSessionObserver() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
